'use client';
import { JSX, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Autoplay } from 'swiper/modules';

// components
import { SwiperSlide } from 'swiper/react';
import { AtCarousel } from '@/components/atoms/at-carousel/at-carousel';
import { AtMedia } from '@/components/atoms/at-media/at-media';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtText } from '@/components/atoms/at-text/at-text';

// types
import {
  EClickableTypes,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { IOrCarouselHeroProps, ISlide } from './or-carousel-hero.types';
import { EImageIntents } from '@/components/atoms/at-media/at-media.types';

export const OrCarouselHero = ({
  autoplay = false,
  className,
  slideData,
  swiperRef,
  textContent,
  onSwiper,
}: IOrCarouselHeroProps): JSX.Element => {
  const [isAutoplayRunning, setIsAutoplayRunning] = useState(autoplay);

  useEffect(() => {
    const swiper = swiperRef?.current;

    if (!swiper?.autoplay || !autoplay) return;

    const updateRunningState = () => {
      setIsAutoplayRunning(swiper.autoplay?.running ?? false);
    };

    const handleUserSwipe = () => {
      swiper.autoplay.stop();
      setIsAutoplayRunning(false);
    };

    swiper.on('touchStart', handleUserSwipe);
    swiper.on('autoplayStart', updateRunningState);
    swiper.on('autoplayStop', updateRunningState);

    updateRunningState();

    return () => {
      swiper.off('touchStart', handleUserSwipe);
      swiper.off('autoplayStart', updateRunningState);
      swiper.off('autoplayStop', updateRunningState);
    };
  }, [swiperRef, autoplay]);

  const handleToggleAutoplay = () => {
    const swiper = swiperRef?.current;
    if (!swiper?.autoplay || !autoplay) return;

    if (swiper.autoplay.running) {
      swiper.autoplay.stop();
    } else {
      swiper.autoplay.start();
    }
  };

  return (
    // todo: update theme to dynamic val
    <div
      data-theme="light"
      className={className}
      data-testid="or-carousel-hero"
    >
      <div className="overflow-right grid grid-cols-12 pt-15">
        <div className="col-span-12 mb-9.5 md:col-span-6 lg:mb-0 xl:col-span-3">
          {textContent}
        </div>

        <div className="relative col-span-12 md:col-span-6 xl:col-span-8 xl:col-start-5">
          <AtCarousel
            autoplayControl={{
              isRunning: isAutoplayRunning,
              onToggle: handleToggleAutoplay,
            }}
            modules={[Autoplay]}
            buttonPosition="hero"
            containerClasses="mb-6"
            buttonTheme="dark"
            // swiper props
            autoplay={autoplay ? { delay: 5000 } : false}
            slidesPerView={1}
            spaceBetween={48}
            speed={600}
            swiperRef={swiperRef}
            breakpoints={{
              768: {
                slidesPerView: 1.06,
                spaceBetween: 18,
              },
              1024: {
                slidesPerView: 1.1,
              },
              1760: {
                slidesPerView: 1,
              },
            }}
            onSwiper={(swiper) => {
              if (swiperRef) {
                swiperRef.current = swiper;
              }

              if (typeof onSwiper === 'function') {
                onSwiper(swiper);
              }
            }}
          >
            {slideData.map((slide: ISlide, i: number) => {
              const { button, disclaimer, header, image } = slide;

              const interactLocationValue = header
                ?.toLowerCase()
                .includes('video')
                ? 'video-tour'
                : 'self-guided-tour';

              return (
                <SwiperSlide
                  className={classNames(
                    'aspect-[1.53] max-h-711 overflow-hidden rounded-3xl',
                  )}
                  key={i}
                >
                  <div
                    data-theme="dark"
                    data-testid="slide"
                    className="flex !h-full w-full flex-col items-center justify-center"
                  >
                    <AtMedia
                      sizes="(max-width: 1023px) 100vw, 40vw"
                      src={image?.src}
                      alt={image?.alt}
                      intent={EImageIntents.BACKGROUND}
                      priority
                    />

                    {header && button && (
                      <div className="pointer-events-none absolute inset-0 rounded-3xl bg-video-overlay"></div>
                    )}

                    <div className="z-10 flex flex-col items-center justify-center">
                      {header && (
                        <div className="text-balance text-center">
                          <AtHeader
                            variant="h3"
                            element="h2"
                            alt
                            className="mb-8 text-skin-media"
                          >
                            {header}
                          </AtHeader>
                        </div>
                      )}
                      {button && (
                        <AtClickable
                          type={EClickableTypes.CTA}
                          label={button.text}
                          variant={EClickableVariants.PRIMARY}
                          outline
                          href={button.url}
                          interactLocation={interactLocationValue}
                        />
                      )}
                      {disclaimer && (
                        <AtText
                          size="sm"
                          className="absolute bottom-0 left-1/2 -translate-x-1/2 whitespace-nowrap pb-5 text-skin-media md:left-0 md:translate-x-0 md:pl-8"
                        >
                          {disclaimer}
                        </AtText>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </AtCarousel>
        </div>
      </div>
    </div>
  );
};
